import { Fragment, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Logo,
  MenuGroup,
  MenuGroupLabel,
  MobileHeader,
  MobileSheet,
  MobileSheetContent,
  MobileSheetSection,
  MobileTab,
  MobileTabBar,
  MobileTabLabel,
  Rail,
  RailFlyout,
  RailFlyoutContent,
  RailFlyoutItem,
  RailFlyoutTile,
  RailFlyoutTrigger,
  RailLabel,
  RailLogo,
  mobileSheetItemVariants,
  mobileTabVariants,
  railLinkVariants,
  useAtLeast,
} from '@wundo/ui';
import type { CurrentUser } from '@wundo/client/auth.ts';
import { activePath } from '@/lib/navMatch.ts';
import {
  activeGroup,
  groupHome,
  groupItems,
  visibleGroups,
  type NavGroup,
} from './navConfig.ts';
import { GlobalOrganisationPicker } from './GlobalOrganisationPicker.tsx';
import { NavItemLabel } from './NavItemLabel.tsx';
import { UserMenu } from './UserMenu.tsx';

/* The admin console's own mark: the Wundo tile with its ADMIN band. */
const MARK = { title: 'Wundo Admin', band: 'ADMIN' };

function useCurrentPath(): string {
  return useRouterState({ select: (state) => state.location.pathname });
}

/**
 * The page of a group the current path belongs to — the longest match, so
 * `/dashboard` does not steal the mark from `/dashboard/metrics-history` and no
 * list ever says the page is in two places at once.
 */
function currentPage(group: NavGroup, pathname: string): string | undefined {
  return activePath(
    pathname,
    groupItems(group).map((item) => item.to),
  );
}

export interface AdminNavigationProps {
  /** Absent until the current user has been fetched — the rail renders anyway. */
  user: CurrentUser | undefined;
  /** Whether the pages that only make sense from inside Wundo are on offer. */
  internal: boolean;
}

/* The tile itself: the icon, its name, and where clicking it lands. */
function GroupTile({ group, active }: { group: NavGroup; active: boolean }) {
  const { icon: Icon } = group;

  return (
    <Link
      /* The tile lands on the group's first page — a group has no page of its own. */
      to={groupHome(group)}
      /*
       * The router's own active match is fuzzy and wins over the prop below;
       * pinned to exact, which tile is current is `activeGroup`'s answer alone.
       */
      activeOptions={{ exact: true }}
      /* Same words as the visible label, so the name matches what is on screen. */
      aria-label={group.label}
      aria-current={active ? 'page' : undefined}
      className={railLinkVariants({ active })}
    >
      {/* DEV-36 settled the icon size at 20 — the 52px row math depends on it. */}
      <Icon className='size-5 shrink-0' aria-hidden='true' />
      <RailLabel>{group.label}</RailLabel>
    </Link>
  );
}

/*
 * The group's pages, as the flyout lists them. Admin's five named runs get
 * their headings; every other group reads as one list and has none.
 */
function GroupPages({ group, pathname }: { group: NavGroup; pathname: string }) {
  const current = currentPage(group, pathname);

  return (
    <>
      {group.sections.map((section, index) => (
        <MenuGroup key={section.title ?? index}>
          {section.title && <MenuGroupLabel>{section.title}</MenuGroupLabel>}
          {section.items.map((item) => (
            <RailFlyoutItem
              key={item.to}
              active={item.to === current}
              render={
                <Link
                  to={item.to}
                  activeOptions={{ exact: true }}
                  aria-current={item.to === current ? 'page' : undefined}
                />
              }
            >
              <NavItemLabel item={item} />
            </RailFlyoutItem>
          ))}
        </MenuGroup>
      ))}
    </>
  );
}

export function AdminRail({ user, internal }: AdminNavigationProps) {
  const pathname = useCurrentPath();
  const groups = visibleGroups(internal);
  const current = activeGroup(groups, pathname);
  /*
   * One at a time, held here rather than per tile: hovering the next group has
   * to put the last one away, or a slow pointer down the rail leaves a trail of
   * popups behind it.
   */
  const [openGroup, setOpenGroup] = useState<string | undefined>(undefined);

  /*
   * From here up the group's pages stand in a column of their own beside the
   * content, so the tile has nothing left to open: no arrow, and no popup on
   * hover — a menu appearing next to a list of the same pages is noise. The
   * same answer decides which of the two the shell renders, so the tile and the
   * column cannot disagree about which mode this is.
   */
  const desk = useAtLeast('desk');
  /* Dragged wide with a popup open: it is now standing beside its own column. */
  if (desk && openGroup !== undefined) setOpenGroup(undefined);

  return (
    <Rail
      aria-label='Main navigation'
      logo={
        <Link to='/' aria-label='Home'>
          <RailLogo {...MARK} />
        </Link>
      }
      footer={<UserMenu user={user} />}
    >
      {/*
       * Six tiles, and no scrolling: a tile is a group, and below the desk
       * breakpoint the pages inside it are in the popup that opens beside it.
       * They were a column of their own on every screen until it turned out to
       * cost a laptop a fifth of its width; a desk has the width to spare.
       */}
      {groups.map((group) => {
        const tile = <GroupTile group={group} active={group.id === current?.id} />;

        /*
         * A bare tile where there is nothing for it to open: the column is
         * already showing the pages, or the group holds a single one — and one
         * page is not a list, so an arrow would lie about it.
         */
        if (desk || groupItems(group).length < 2) {
          return <Fragment key={group.id}>{tile}</Fragment>;
        }

        return (
          <RailFlyout
            key={group.id}
            open={openGroup === group.id}
            onOpenChange={(open) => setOpenGroup(open ? group.id : undefined)}
          >
            <RailFlyoutTile>
              {tile}
              {/*
               * Named for what it opens rather than for the group, so it is not
               * a second link by the same name as the tile it sits on.
               */}
              <RailFlyoutTrigger aria-label={`${group.label} pages`} />
            </RailFlyoutTile>
            <RailFlyoutContent aria-label={group.label}>
              <GroupPages group={group} pathname={pathname} />
            </RailFlyoutContent>
          </RailFlyout>
        );
      })}
    </Rail>
  );
}

/* The phone's top bar: the mark home, the organisation picker, and you. */
export function AdminMobileHeader({ user }: Pick<AdminNavigationProps, 'user'>) {
  return (
    <MobileHeader>
      <Link to='/' aria-label='Home'>
        <Logo size='md' {...MARK} />
      </Link>
      <GlobalOrganisationPicker className='mx-3 min-w-0 flex-1' />
      <UserMenu user={user} side='bottom' />
    </MobileHeader>
  );
}

/*
 * The rail's tiles along the bottom of a phone. A tile opens a sheet with its
 * group's pages — the flyout's job on a laptop — except where the group holds
 * a single page: one page is not a list, so that tile goes straight there.
 */
export function AdminTabBar({ internal }: Pick<AdminNavigationProps, 'internal'>) {
  const pathname = useCurrentPath();
  const groups = visibleGroups(internal);
  const current = activeGroup(groups, pathname);
  /*
   * The group stays set while the sheet slides away, so its pages do not
   * vanish out from under the closing animation.
   */
  const [sheet, setSheet] = useState<{ group: NavGroup; open: boolean } | undefined>(
    undefined,
  );
  const close = () => setSheet((shown) => shown && { ...shown, open: false });

  return (
    <>
      <MobileTabBar aria-label='Main navigation'>
        {groups.map((group) => {
          const { icon: Icon } = group;
          const active = group.id === current?.id;
          const content = (
            <>
              <Icon aria-hidden='true' />
              <MobileTabLabel>{group.label}</MobileTabLabel>
            </>
          );

          if (groupItems(group).length < 2) {
            return (
              <Link
                key={group.id}
                to={groupHome(group)}
                activeOptions={{ exact: true }}
                aria-label={group.label}
                aria-current={active ? 'page' : undefined}
                className={mobileTabVariants({ active })}
              >
                {content}
              </Link>
            );
          }

          return (
            <MobileTab
              key={group.id}
              active={active}
              aria-label={group.label}
              aria-current={active ? 'page' : undefined}
              aria-haspopup='dialog'
              onClick={() => setSheet({ group, open: true })}
            >
              {content}
            </MobileTab>
          );
        })}
      </MobileTabBar>

      <MobileSheet
        open={sheet?.open ?? false}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        {sheet && (
          <MobileSheetContent title={sheet.group.label}>
            <GroupSheetPages group={sheet.group} pathname={pathname} onNavigate={close} />
          </MobileSheetContent>
        )}
      </MobileSheet>
    </>
  );
}

/* A group's pages in its sheet, Admin's runs under their headings. */
function GroupSheetPages({
  group,
  pathname,
  onNavigate,
}: {
  group: NavGroup;
  pathname: string;
  onNavigate: () => void;
}) {
  const current = currentPage(group, pathname);

  return (
    <nav aria-label={`${group.label} pages`}>
      {group.sections.map((section, index) => (
        <MobileSheetSection key={section.title ?? index} title={section.title}>
          {section.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: true }}
              aria-current={item.to === current ? 'page' : undefined}
              onClick={onNavigate}
              className={mobileSheetItemVariants({ active: item.to === current })}
            >
              <NavItemLabel item={item} />
            </Link>
          ))}
        </MobileSheetSection>
      ))}
    </nav>
  );
}
